// Sourced power and cooling options lead; readiness and the workspace follow.
// Pure presentation: values and action authority come from the host.
import React, { useMemo } from 'react';
import {
  Target,
  MapPin,
  FileText,
  GitBranch,
  ShieldCheck,
  Package,
  BookOpen,
  FolderOpen,
  Activity,
  Factory,
  BarChart3,
  CheckCircle2,
  Building2,
  ArrowRight,
  ChevronDown,
  Pencil,
  Microscope,
  type LucideIcon,
} from 'lucide-react';
import type { SiteEntity } from '../../../types/site';
import type { SiteRagReport, SiteDocument } from '../../../types/siteRag';
import type { ProjectIntent } from '../../../types/siteRag';
import type { SiteEvidenceDocument } from '../../../types/siteEvidence';
import type { DerivedSiteVerdict } from '../../../lib/portfolio/deriveSiteVerdict';
import { displayAddress } from '../../../lib/siteAddressDisplay';
import { effectivePhases } from '../../../lib/projectIntent';
import { effectiveWorkloadProfile } from '../../../lib/workloadProfile';
import { workloadProfileLabel } from '../WorkloadProfilePicker';
import type {
  PathToGoPhaseId,
  PathToGoState,
} from '../../../lib/pathToGo/pathToGoOrchestrator';
import type { NextAction } from '../../../lib/pathToGo/nextAction';
import { VERDICT_WITHHELD_LABEL } from '../../../lib/portfolio/decisionBands';
import type { SiteComposite } from '../../../types/portfolioComposite';
import { buildStageBoards, withScreeningOutcome } from '../../../lib/pathToGo/stageBoards';
import { buildSiteNextStep } from '../../../lib/pathToGo/siteNextStep';
import { DecisionPath } from '../../ui/instrument/DecisionPath';
import { resolveIntentPowerFigure, formatPowerBandLayer } from '../../../lib/powerStack';
import { resolvePromotedGenerationScenario } from '../../../lib/generationImpact/promotedScenario';
import { buildCallReadyFindingsModel } from '../../../lib/findings/callReadyFindings';
import { SiteRecommendedPath, selectCompletedScreeningReport } from './SiteRecommendedPath';
import type { SiteDecisionStageStatus } from '../../../lib/stage2/siteDecisionStage';
import { isIntentFieldExplicitlyUnknown } from '../../../lib/intent/explicitUnknown';

/** Concise "Nuclear interest" KPI labels (mirror ProjectIntentForm's map). */
const NUCLEAR_INTEREST_KPI: Record<NonNullable<ProjectIntent['nuclearInterest']>, string> = {
  primary: 'Primary plan',
  considering: 'Considering',
  no: 'Not interested',
  undecided: 'Undecided',
};

/** The tab ids SiteHomeTab can navigate to. Kept as a string literal union
 *  that is a structural subset of the host's `Tab` type — the host passes its
 *  own `setActiveTab` as `onNavigate`, so any id here must be a real tab. */
export type SiteHomeNavTab =
  | 'intent'
  | 'map'
  | 'reports'
  | 'decision'
  | 'stakeholder'
  | 'roadmap'
  | 'audit'
  | 'deepdive'
  | 'supplychain'
  | 'evidence'
  | 'documents'
  | 'simulations'
  | 'generation'
  | 'financial'
  | 'deepdive';

export interface SiteHomeTabProps {
  site: SiteEntity;
  composite?: SiteComposite | null;
  siteReports: SiteRagReport[];
  /** Same semantic projections/readiness overlays used by the sourced brief. */
  recommendationReports?: readonly SiteRagReport[];
  recommendationDecisionStage?: Pick<SiteDecisionStageStatus, 'stage1Complete' | 'gridOnly'> | null;
  siteDocuments: SiteDocument[];
  /** The page-level SED (when loaded). Drives the "Site Evidence" card status. */
  sed?: SiteEvidenceDocument;
  derivedVerdict: DerivedSiteVerdict;
  /** Jump to a top-level tab. The host passes `setActiveTab`. */
  onNavigate: (tab: SiteHomeNavTab) => void;
  /**
   * Open the work of a journey phase — the host routes each phase to the tab
   * that owns its first open item, and Decide (or no phase) to the Decide
   * surface. Absent → a plain tab jump per phase (see `phaseFallbackTab`).
   */
  onOpenPhase?: (phase?: PathToGoPhaseId) => void;
  /**
   * Run the journey band's ONE next action (the host dispatches its typed
   * plan action). Absent → the CTA opens the action's phase instead.
   */
  onRunNextAction?: (action: NextAction) => void;
  /** Keep the home destination aligned with the host's Generation & Impact
   *  feature flag so a card is never shown for a hidden tab. */
  showGenerationImpact?: boolean;
  /** Hide Engineering Analysis while simulation compute is intentionally frozen. */
  showEngineeringAnalysis?: boolean;
  /** Whether Reports has become the live Analyses workspace. */
  showAnalysesWorkspace?: boolean;
  /** Keep promoted Decision / Stakeholder cards aligned with the host flags. */
  showDecisionSurfaces?: boolean;
  /** Distinct live analysis types, including semantic-only revisions. */
  analysisCount?: number;
  /** Open the inline rename editor (host wires this to its existing
   *  setEditName/setIsEditingName). Omitted/disabled when read-only. */
  onEditName?: () => void;
  /** True when the site is locked read-only for this viewer — disables rename. */
  renameDisabled?: boolean;
  /** Title shown on the rename pencil (e.g. lock reason). */
  renameTitle?: string;
  /** Site edit-lock controls, rendered in the hero's top-right. The host passes
   *  its `<SiteLockControls site={site} />` so this stays presentational. */
  lockControls?: React.ReactNode;

  onViewReport: (reportId: string) => void;

  /* ── Journey visualization (design pass A) ── */
  /** The derived Path-to-GO state + next action for the journey band under
   *  the hero. Host-computed (this component stays store-free); null hides
   *  the band. */
  journey?: { state: PathToGoState; nextAction: NextAction; loading?: boolean } | null;
  /** Per-tab journey dots (same pure `tabStatus` module as the tab strip) —
   *  drives the section-card status pills. */
  tabStatuses?: Partial<Record<string, 'cleared' | 'open'>>;
}

interface HomeCard {
  tab: SiteHomeNavTab;
  title: string;
  description: string;
  icon: LucideIcon;
  /** Optional count / status pill text shown in the card foot. */
  pill?: string;
  /** Variant for a status-style pill; omit for a neutral count chip. */
  pillVariant?: 'success' | 'neutral';
  /** Tiny entrance-stagger index (CSS animation-delay). */
  delay: number;
}

interface HomeBand {
  label: string;
  cards: HomeCard[];
}

/** Format a MW figure as e.g. "1,750". Returns null when not set. */
function formatMw(mw: number | undefined): string | null {
  if (typeof mw !== 'number' || !Number.isFinite(mw)) return null;
  return Math.round(mw).toLocaleString('en-US');
}

/** Zero-padded "NN sections" band-count label, mirroring the mockup. */

/**
 * SiteHomeTab — the Instrument Panel home.
 *
 * Pure presentation: every datum comes from props, every action routes
 * through `onNavigate`. No store reads, no fetches.
 */
export const SiteHomeTab: React.FC<SiteHomeTabProps> = ({
  site,
  siteReports,
  recommendationReports = siteReports,
  recommendationDecisionStage,
  siteDocuments,
  sed,
  derivedVerdict,
  onNavigate,
  onOpenPhase,
  onRunNextAction,
  showGenerationImpact = true,
  showEngineeringAnalysis = true,
  showAnalysesWorkspace = true,
  showDecisionSurfaces = true,
  analysisCount,
  onEditName,
  renameDisabled = false,
  renameTitle,
  lockControls,
  journey = null,
  tabStatuses = {},
}) => {
  const location = displayAddress(site);
  const reportCount = analysisCount ?? siteReports.length;
  const documentCount = siteDocuments.length;
  // SED version: show the LATEST version known from EITHER the loaded SED doc
  // OR the site's persisted `sedVersion` (the authoritative pointer bumped on
  // every rebuild). The old `sed?.version ?? site.sedVersion` PREFERRED the
  // loaded doc, so a stale lazily-loaded v2 SED payload pinned the label to
  // "SED v2" on a site already rebuilt to v4. Taking the max means a stale
  // cached SED can never under-report the current version. Null when neither
  // source has a SED yet.
  const sedVersion = (() => {
    const fromSed = typeof sed?.version === 'number' ? sed.version : null;
    const fromSite = typeof site.sedVersion === 'number' ? site.sedVersion : null;
    if (fromSed == null && fromSite == null) return null;
    return Math.max(fromSed ?? 0, fromSite ?? 0);
  })();
  const sedVersionLabel = sedVersion != null ? `v${sedVersion}` : '—';

  // ── Headline KPIs — Power Target · Phases · Nuclear interest · Workload ──
  // All sourced from the resolved project intent (the same helpers the intent
  // form + reports use), so the hero never drifts from the strategy.
  const intent = site.projectIntent;
  const phasedView = effectivePhases(intent);
  const phaseCount = phasedView.phases.length;
  const nuclearLabel = intent?.nuclearInterest
    ? NUCLEAR_INTEREST_KPI[intent.nuclearInterest]
    : '—';
  const workloadLabel = workloadProfileLabel(effectiveWorkloadProfile(intent));

  // Verdict pill: the derived band drives both the StatusPill variant and the
  // dot colour so the home pill cannot drift from Overview / portfolio.
  const verdictLabel = derivedVerdict.dataAdequate
    ? (derivedVerdict.bandLabel || VERDICT_WITHHELD_LABEL)
    : VERDICT_WITHHELD_LABEL;
  const decisionHomeTab: SiteHomeNavTab = showDecisionSurfaces ? 'decision' : 'reports';
  /** Plain tab per phase when the host supplies no phase router. */
  const phaseFallbackTab = (phase?: PathToGoPhaseId): SiteHomeNavTab => {
    if (phase === 'define') return 'intent';
    if (phase === 'build') return 'reports';
    if (phase === 'verify') return 'evidence';
    return decisionHomeTab;
  };
  const openPhase = (phase?: PathToGoPhaseId) => {
    if (onOpenPhase) onOpenPhase(phase);
    else onNavigate(phaseFallbackTab(phase));
  };

  // The tab dots track work present, not engineering approval. Describe that
  // actual state instead of calling every saved model or report "Done".
  const statusPill = (tab: string): Pick<HomeCard, 'pill' | 'pillVariant'> => {
    const s = tabStatuses[tab];
    if (tab === 'decision') return derivedVerdict.dataAdequate && derivedVerdict.band
      ? { pill: 'Ready to review', pillVariant: 'neutral' }
      : { pill: VERDICT_WITHHELD_LABEL, pillVariant: 'neutral' };
    if (tab === 'intent') return s === 'cleared'
      ? { pill: 'Defined', pillVariant: 'success' }
      : { pill: site.projectIntent ? 'Refine details' : 'Optional for screening', pillVariant: 'neutral' };
    if (s === 'cleared') {
      const savedLabel: Record<string, string> = { simulations: 'Saved', financial: 'Inputs saved', stakeholder: 'Available', deepdive: 'Available', map: 'Mapped' };
      if (savedLabel[tab]) return { pill: savedLabel[tab], pillVariant: 'neutral' };
    }
    if (s === 'cleared') return { pill: 'Done', pillVariant: 'success' };
    if (s === 'open') return { pill: 'To do', pillVariant: 'neutral' };
    return {};
  };

  // Bands mirror the tab strip's FUNNEL grouping (design pass A): Define →
  // Analyze (project modelling and validation) → Decide. The journey band
  // above carries the phases and the one next action.
  const bands = useMemo<HomeBand[]>(() => [
    {
      // DEFINE — set up the project: what it is, where, the facts & kit.
      label: 'Define',
      cards: [
        {
          tab: 'intent',
          title: 'Intent',
          description: 'Power, cooling & phasing strategy.',
          icon: Target,
          ...statusPill('intent'),
          delay: 1,
        },
        {
          tab: 'map',
          title: 'Map',
          description: 'Parcel boundary, layers & geospatial.',
          icon: MapPin,
          ...statusPill('map'),
          delay: 2,
        },
        {
          tab: 'documents',
          title: 'Documents',
          description: 'Upload & manage diligence docs.',
          icon: FolderOpen,
          pill: String(documentCount),
          pillVariant: 'neutral',
          delay: 3,
        },
        {
          tab: 'evidence',
          title: 'Site Evidence',
          description: 'The versioned factual record (SED).',
          icon: BookOpen,
          pill: site.sedStatus === 'bootstrapping' ? `Updating${sedVersion != null ? ` · SED ${sedVersionLabel}` : ''}`
            : site.sedStatus === 'failed' ? 'Rebuild needs attention'
            : sedVersion != null ? `SED ${sedVersionLabel}` : 'No SED yet',
          pillVariant: sedVersion != null && site.sedStatus === 'complete' ? 'success' : 'neutral',
          delay: 4,
        },
      ],
    },
    {
      // ANALYZE — tools supply the applicable mature-validation prerequisites.
      label: 'Analyze',
      cards: [
        ...(showEngineeringAnalysis ? [{
          tab: 'simulations',
          title: 'Engineering',
          description: 'Simulations & engineering analysis.',
          icon: Activity,
          ...statusPill('simulations'),
          delay: 5,
        } as HomeCard] : []),
        ...(showGenerationImpact ? [{
          tab: 'generation' as const,
          title: 'Generation & Impact',
          description: 'Capacity scenarios, land use & site impact.',
          icon: Factory,
          ...statusPill('generation'),
          delay: 6,
        }] : []),
        {
          tab: 'financial',
          title: 'Financial',
          description: 'LCOE, NPV, IRR & the financial model.',
          icon: BarChart3,
          ...statusPill('financial'),
          delay: 7,
        },
        {
          tab: 'supplychain',
          title: 'Equipment (BOM)',
          description: 'Bill of materials, suppliers & CAPEX.',
          icon: Package,
          ...statusPill('supplychain'),
          delay: 8,
        },
      ],
    },
    {
      // DECIDE — verdict, integrity, and post-decision execution (Roadmap
      // last: it tracks the recommendations your reports produced).
      label: 'Decide',
      cards: [
        {
          tab: 'reports',
          title: showAnalysesWorkspace ? 'Analyses' : 'Reports',
          description: showAnalysesWorkspace
            ? 'Review current analyses, evidence and constraints.'
            : 'Review generated reports and their history.',
          icon: FileText,
          pill: String(reportCount),
          pillVariant: 'neutral',
          delay: 9,
        },
        ...(showDecisionSurfaces ? [
          {
            tab: 'decision' as const,
            title: 'Decision',
            description: 'Review readiness, the current recommendation and next action.',
            icon: CheckCircle2,
            ...statusPill('decision'),
            delay: 10,
          },
          {
            tab: 'stakeholder' as const,
            title: 'Stakeholder Brief',
            description: 'Use the sourced, audience-ready screening answer sheet.',
            icon: Building2,
            ...statusPill('stakeholder'),
            delay: 11,
          },
        ] : []),
        {
          tab: 'audit',
          title: 'Audit',
          description: 'Integrity checks, provenance & activity.',
          icon: ShieldCheck,
          ...statusPill('audit'),
          delay: 12,
        },
        {
          tab: 'deepdive',
          title: 'Scoring Engine',
          description: 'Trace every score directly to its source rationale and citations.',
          icon: Microscope,
          ...statusPill('deepdive'),
          delay: 13,
        },
        {
          tab: 'roadmap',
          title: 'Roadmap',
          description: 'Track execution of the recommendations your reports produced.',
          icon: GitBranch,
          ...statusPill('roadmap'),
          delay: 14,
        },
      ],
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- statusPill reads only the explicit dependencies below
  ], [reportCount, documentCount, sedVersion, sedVersionLabel, site.sedStatus, site.projectIntent, derivedVerdict.dataAdequate, derivedVerdict.band, showEngineeringAnalysis, showGenerationImpact, showAnalysesWorkspace, showDecisionSurfaces, tabStatuses]);

  const boards = journey ? withScreeningOutcome(buildStageBoards(journey.state.reportPlan), journey.state.decisionRollup?.stages) : null;
  const step = journey && boards ? buildSiteNextStep(journey.nextAction, boards) : null;
  const powerFigure = resolveIntentPowerFigure(intent);
  const promoted = resolvePromotedGenerationScenario(intent, site.promotedGenerationScenarioSnapshot, null, site.generationAssessmentSelection, site.generationProjectInputBinding);
  const firmMW = promoted?.result?.capacity?.contingencyNetMW;
  const contingency = promoted?.result?.capacity?.contingencyOutages;
  const firmBasisLabel = typeof contingency === 'number' && Number.isInteger(contingency) && contingency >= 0
    ? `Firm delivery at N-${contingency}` : 'Firm delivery';
  const firmLabel = typeof firmMW === 'number' && Number.isFinite(firmMW) && firmMW >= 0
    ? `${formatMw(firmMW)} MW` : 'Not recorded';
  const powerLane = (lane: 'itLoadMW' | 'facilityMW') => powerFigure.kind === 'exact'
    ? powerFigure.stack[lane] == null ? 'Not recorded' : `${formatMw(powerFigure.stack[lane]!)} MW`
    : powerFigure.kind === 'band' ? formatPowerBandLayer(powerFigure.band[lane], value => Math.round(value).toLocaleString('en-US')) : 'Not recorded';
  const basisRows = [
    ['power', 'Power approach', ({ 'grid-only': 'Grid only', hybrid: 'Hybrid grid and on-site supply', 'btm-only': 'Behind-the-meter only', islanded: 'Islanded / off-grid' } as Record<string, string>)[site.typedPreferencesDefaults?.['power-availability']?.btmStrategy ?? ''] ?? 'Not yet determined'],
    ['phases', 'Phasing', isIntentFieldExplicitlyUnknown(intent, 'phases') ? 'Undecided' : intent ? `${phaseCount} ${phaseCount === 1 ? 'phase' : 'phases'}` : 'Not recorded'],
    ['nuclear', 'Nuclear interest', nuclearLabel],
    ['workload', 'Workload', workloadLabel],
  ];
  // Reuse the governed answer builder: Home cannot become a second recommender.
  // It checks SED identity/trust, report holds and promotion stamps itself.
  const recommendedPath = useMemo(() => buildCallReadyFindingsModel(site, recommendationReports, {
    sed: sed ?? null,
    economicContributionEnabled: false,
    analysesState: journey?.loading ? 'loading' : 'settled',
    decisionStage: recommendationDecisionStage,
  }), [site, recommendationReports, recommendationDecisionStage, sed, journey?.loading]);
  const shortcuts = bands.flatMap(band => band.cards).filter(card =>
    ['intent', 'documents', 'evidence', 'reports', 'generation', 'financial'].includes(card.tab));
  const hasScreeningRecommendations = selectCompletedScreeningReport(recommendationReports, site.id) != null;
  return (
    <div className="space-y-5 p-4 md:p-6" data-testid="site-home-tab">
      <header className="flex flex-wrap items-center gap-3" data-testid="site-home-hero">
        <div className="min-w-0 flex-1"><h1 className="type-section text-text-primary">{site.name}</h1>{location && <p className="mt-1 type-meta text-text-muted">{location}</p>}</div>
        {onEditName && <button type="button" onClick={onEditName} disabled={renameDisabled} title={renameTitle ?? 'Rename site'} aria-label="Rename site" className="text-text-secondary focus-visible:ring-2 focus-visible:ring-primary" data-testid="site-home-name-edit-button"><Pencil className="h-4 w-4" /></button>}
        {lockControls && <div data-testid="site-home-lock-controls">{lockControls}</div>}
      </header>
      {journey && boards && step ? <DecisionPath boards={boards} step={step} loading={journey.loading} decisionLabel={derivedVerdict.dataAdequate && derivedVerdict.band ? derivedVerdict.bandLabel : undefined} onRun={() => onRunNextAction ? onRunNextAction(journey.nextAction) : openPhase(journey.nextAction.targetPhase)} canOpenStation={station => station !== 'interlock' || showGenerationImpact} onOpenStation={station => station === 'decision' ? openPhase('decide') : onNavigate(station === 'interlock' ? 'generation' : 'reports')} /> : <section className="instrument-panel"><p className="type-meta text-text-muted" data-testid="site-home-verdict">{verdictLabel}</p><button type="button" data-testid="site-home-decision-link" className="mt-2 type-body text-primary" onClick={() => openPhase('decide')}>Review decision</button></section>}
      <section className="instrument-panel" data-testid="site-home-basis">
        <header className="instrument-section-head"><div><h2 className="type-section text-text-primary">Project basis</h2><p className="type-meta text-text-muted">What the generation scenario has to serve</p></div><button type="button" className="type-meta text-primary" onClick={() => onNavigate('intent')}>Open Intent</button></header>
        <dl className="grid gap-5 py-5 sm:grid-cols-3"><div data-testid="site-home-basis-lane-it"><dt className="type-meta text-text-muted">IT load</dt><dd className="type-section text-text-primary mt-1">{powerLane('itLoadMW')}</dd></div><div data-testid="site-home-basis-lane-facility"><dt className="type-meta text-text-muted">Facility demand</dt><dd className="type-section text-text-primary mt-1">{powerLane('facilityMW')}</dd></div><div data-testid="site-home-basis-lane-firm"><dt className="type-meta text-text-muted">{firmBasisLabel}</dt><dd className="type-section text-text-primary mt-1">{firmLabel}</dd><p className="type-meta text-text-muted mt-1">{firmLabel !== 'Not recorded' ? 'Promoted scenario · generation terminals' : 'From a promoted generation scenario'}</p></div></dl>
        {powerFigure.kind === 'band' && <p className="type-meta text-text-muted mb-4">Declared range · bucket-derived, {powerFigure.band.basis === 'it-load' ? 'IT load' : 'facility'} basis</p>}
        <details className="group border-t border-border" data-testid="site-home-basis-details">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-3 py-3 type-meta text-text-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary [&::-webkit-details-marker]:hidden">Project details<ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" aria-hidden /></summary>
          <dl className="divide-y divide-border pb-2">{basisRows.map(([key,label,value]) => <div key={key} className="flex justify-between gap-4 py-2" data-testid={`site-home-basis-row-${key}`}><dt className="type-meta text-text-muted">{label}</dt><dd className="type-body text-text-primary text-right">{value}</dd></div>)}</dl>
        </details>
      </section>
      {hasScreeningRecommendations && <details className="instrument-panel group" data-testid="site-home-recommendations-details">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-3 type-body font-medium text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary [&::-webkit-details-marker]:hidden">Screening recommendations<ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" aria-hidden /></summary>
        <div className="mt-5"><SiteRecommendedPath
          screeningReports={recommendationReports}
          siteId={site.id}
          liveSite={site}
          answer={recommendedPath.questions.find(answer => answer.id === 'resources')}
          risks={recommendedPath.questions.find(answer => answer.id === 'power-cooling-risks')?.risks}
          loading={recommendedPath.recordState === 'loading'}
          onReview={() => onNavigate(showDecisionSurfaces ? 'stakeholder' : 'reports')}
        /></div>
      </details>}
      <section className="instrument-panel" data-testid="site-home-workspace"><header className="instrument-section-head"><h2 className="type-section text-text-primary">Workspace</h2></header>
        <nav aria-label="Project workspace" className="grid gap-x-6 gap-y-1 py-3 sm:grid-cols-2 lg:grid-cols-3">
          {shortcuts.map(card => { const Icon = card.icon; return <button type="button" key={card.tab} onClick={() => onNavigate(card.tab)} className="flex min-h-11 items-center gap-3 rounded-sm px-1 text-left type-body text-text-primary hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary" data-testid={`site-home-shortcut-${card.tab}`}><Icon className="h-4 w-4 shrink-0 text-text-muted" aria-hidden />{card.title}<ArrowRight className="ml-auto h-4 w-4 shrink-0 text-text-muted" aria-hidden /></button>;})}
        </nav>
        <details className="group border-t border-border" data-testid="site-home-tools-details">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-3 py-3 type-meta text-text-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary [&::-webkit-details-marker]:hidden">All tools<ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" aria-hidden /></summary>
          {bands.map(band => <section key={band.label} data-testid={`site-home-index-group-${band.label.toLowerCase()}`} className="mt-4"><h3 className="type-meta text-text-muted mb-2">{band.label}</h3>{band.cards.map(card => { const Icon = card.icon; return <button type="button" key={card.tab} onClick={() => onNavigate(card.tab)} className="instrument-workspace-row" data-testid={`site-home-index-${card.tab}`}><Icon className="h-4 w-4 text-text-muted" aria-hidden /><span className="type-body text-text-primary">{card.title}</span><span className="instrument-workspace-description type-meta text-text-muted">{card.description}</span><span className="type-meta text-text-secondary">{card.tab === 'decision' ? 'see path above' : card.pill}</span><ArrowRight className="h-4 w-4 text-text-muted" aria-hidden /></button>;})}</section>)}
        </details>
      </section>
    </div>
  );
};
